import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import TaskDialog from "@/components/TaskDialog";
import { ListPlus, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/utils/date";

const TEAMS = ["Management", "Shoot Team", "Edit Team", "Digital Team", "Sales Team", "Finance Team", "HR + Admin"];

export default function Backlog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [team, setTeam] = useState(user?.is_super_admin ? "Shoot Team" : (user?.team || "Shoot Team"));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Assign-to-member flow
  const [assignFor, setAssignFor] = useState(null); // backlog item currently being promoted
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    try {
      const [b, u] = await Promise.all([api.get("/backlog"), api.get("/users")]);
      setItems(b.data); setUsers(u.data);
    } catch { /* silent */ }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const grouped = useMemo(() => {
    const m = new Map(TEAMS.map((t) => [t, []]));
    items.forEach((it) => {
      if (!m.has(it.team)) m.set(it.team, []);
      m.get(it.team).push(it);
    });
    return m;
  }, [items]);

  const add = async () => {
    if (!title.trim()) { toast.error("Add a title first"); return; }
    setSaving(true);
    try {
      await api.post("/backlog", { title: title.trim(), team, notes: notes.trim() });
      setTitle(""); setNotes("");
      await load();
      toast.success("Added to backlog");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete backlog item "${it.title}"?`)) return;
    try {
      await api.delete(`/backlog/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success("Removed");
    } catch { toast.error("Failed to delete"); }
  };

  const startAssign = (it) => {
    setAssignFor(it);
    setDialogOpen(true);
  };

  // After the task is created via TaskDialog, remove the backlog item automatically.
  const onDialogSaved = async () => {
    if (assignFor) {
      try { await api.delete(`/backlog/${assignFor.id}`); } catch { /* ignore */ }
      setAssignFor(null);
    }
    await load();
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="overline flex items-center gap-2"><ListPlus size={12} /> Backlog</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1 mb-2">Work to be picked up</h1>
      <p className="text-sm text-white/50 mb-6">
        Jot down anything that needs to get done. Promote each item to a full task and assign it to a teammate when you&apos;re ready.
      </p>

      {/* Add row */}
      <div className="glass rounded-xl p-4 mb-6" data-testid="backlog-add-form">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-3">
            <Label className="overline">Work item</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              data-testid="backlog-title-input"
              className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="overline">Team</Label>
            <Select value={team} onValueChange={setTeam}>
              <SelectTrigger
                data-testid="backlog-team-select"
                disabled={!user?.is_super_admin}
                className="bg-black/40 border-white/10 mt-2 disabled:opacity-60"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                {TEAMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={add}
            disabled={saving || !title.trim()}
            data-testid="backlog-add-btn"
            className="bg-red-600 hover:bg-red-500 rounded-full h-10"
          >
            <ListPlus size={14} className="mr-2" /> Add
          </Button>
        </div>
      </div>

      {/* Grouped list */}
      <div className="space-y-6">
        {TEAMS.filter((t) => user?.is_super_admin || user?.is_admin ? (grouped.get(t) || []).length || t === user?.team : t === user?.team).map((t) => {
          const list = grouped.get(t) || [];
          return (
            <div key={t} className="rounded-xl border border-white/8 bg-surface">
              <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Users size={13} className="text-[#E11D2E]" />
                  <div className="font-display uppercase text-sm tracking-wide">{t}</div>
                </div>
                <div className="text-[0.7rem] font-mono text-white/40 uppercase tracking-widest">
                  {list.length} pending
                </div>
              </div>
              {list.length === 0 ? (
                <div className="p-6 text-xs text-white/30 italic text-center">Nothing queued for this team.</div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {list.map((it) => (
                    <li
                      key={it.id}
                      data-testid={`backlog-item-${it.id}`}
                      className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-white/[0.03]"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{it.title}</div>
                        <div className="text-[0.7rem] text-white/40 font-mono mt-0.5">
                          Added by {it.created_by_name} · {fmtDate(it.created_at)}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => startAssign(it)}
                        data-testid={`backlog-assign-${it.id}`}
                        className="bg-[#E11D2E]/15 border border-[#E11D2E]/40 text-white hover:bg-[#E11D2E]/25 rounded-full h-8"
                      >
                        <Send size={12} className="mr-1.5" /> Assign
                      </Button>
                      <button
                        onClick={() => remove(it)}
                        data-testid={`backlog-remove-${it.id}`}
                        className="text-white/40 hover:text-red-400 hover:bg-red-500/10 p-1.5 rounded"
                        aria-label="Delete backlog item"
                      >
                        <Trash2 size={13} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Promote-to-task dialog — pre-fills title from the backlog item */}
      <TaskDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setAssignFor(null); }}
        users={users.filter((u) => user?.is_super_admin || u.team === (assignFor?.team || user?.team))}
        existing={assignFor ? { title: assignFor.title, team: assignFor.team } : null}
        onSaved={onDialogSaved}
      />
    </div>
  );
}
